import type {
  HealthRollupRow,
  HealthView,
  ProviderUsageView,
} from "../api/client.js";

function melbourneTimestamp(value: string | null): string {
  if (value === null) return "No successful call recorded";
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

/**
 * "resets 10:00 am", said in Melbourne time.
 *
 * Both meters count a day, and neither counts the operator's day. Highlightly
 * rolls over with its provider at 00:00 UTC, which is mid-morning here, and
 * the RapidOdds budget is ours and runs to Melbourne midnight. Without this
 * the Highlightly number looks wrong every morning, because it is a number
 * that started counting at ten.
 */
function resetsAt(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsed);
}

/**
 * How each grade reads, and how it looks.
 *
 * The page used to paint HEALTHY green and literally everything else the same
 * yellow, so a component that had never worked in its life was indistinguish-
 * able from one warming up. Eleven of the forty-four were in that state and
 * nobody could tell - including the two that had been saying for three weeks
 * that batter strikeouts are not purchasable.
 */
const SEVERITY: Record<
  HealthRollupRow["severity"],
  { label: string; className: string }
> = {
  BROKEN: { label: "Broken", className: "health-bad" },
  STALE: { label: "Stopped reporting", className: "health-bad" },
  DEGRADED: { label: "Degraded", className: "health-warn" },
  OUT_OF_SEASON: { label: "Out of season", className: "health-muted" },
  HEALTHY: { label: "Healthy", className: "health-ok" },
};

/** "12m ago", at whatever unit is quickest to read. */
function lastTried(value: string | null): string {
  if (value === null) return "never";
  const minutes = (Date.now() - new Date(value).getTime()) / 60_000;
  if (!Number.isFinite(minutes)) return "unknown";
  if (minutes < 90) return `${Math.round(minutes)}m ago`;
  const hours = minutes / 60;
  return hours < 48
    ? `${Math.round(hours)}h ago`
    : `${Math.round(hours / 24)}d ago`;
}

function usageStatus(usage: ProviderUsageView): {
  label: string;
  className: string;
} {
  if (usage.betCriticalExhausted)
    return { label: "Betting blocked", className: "health-bad" };
  if (
    usage.lastFailureAt !== null &&
    (usage.lastSuccessAt === null ||
      new Date(usage.lastFailureAt).getTime() >
        new Date(usage.lastSuccessAt).getTime())
  )
    return { label: "Feed failed", className: "health-bad" };
  if (usage.optionalSamplingStopped)
    return { label: "Optional sampling paused", className: "health-warn" };
  if (usage.lastSuccessAt === null)
    return { label: "Awaiting first check", className: "health-warn" };
  return { label: "Available", className: "health-ok" };
}

/**
 * Sorted by what needs doing. A check that last reported HEALTHY and simply
 * had nothing to do for a day (a conflict audit, a sweep with no work) is
 * idle, not broken - it used to sit in red beside real failures.
 */
export function groupChecks(rows: readonly HealthRollupRow[]) {
  const idle = (row: HealthRollupRow) => badge(row).label === "Idle";
  return {
    attention: rows.filter(
      (row) =>
        row.severity === "BROKEN" || (row.severity === "STALE" && !idle(row)),
    ),
    degraded: rows.filter((row) => row.severity === "DEGRADED"),
    idle: rows.filter(idle),
    outOfSeason: rows.filter((row) => row.severity === "OUT_OF_SEASON"),
    healthy: rows.filter((row) => row.severity === "HEALTHY"),
  };
}

function badge(row: HealthRollupRow): { label: string; className: string } {
  return row.severity === "STALE" && row.reportedStatus === "HEALTHY"
    ? { label: "Idle", className: "health-muted" }
    : SEVERITY[row.severity];
}

function HealthGroup({
  title,
  rows,
  open = false,
}: {
  title: string;
  rows: readonly HealthRollupRow[];
  open?: boolean;
}) {
  if (rows.length === 0) return null;
  return (
    <details open={open}>
      <summary>
        {title} ({rows.length})
      </summary>
      {rows.map((row) => (
        <article key={`${row.sport}-${row.component}`}>
          <div>
            <strong>
              {row.sport} {row.component}
            </strong>
            <small>
              {row.detail}
              {row.neverSucceeded ? " · has never worked" : ""} · last tried{" "}
              {lastTried(row.lastAttemptAt)}
              {row.lastSuccessAt === null || row.severity === "HEALTHY"
                ? ""
                : ` · last worked ${lastTried(row.lastSuccessAt)}`}
            </small>
          </div>
          <span className={badge(row).className}>{badge(row).label}</span>
        </article>
      ))}
    </details>
  );
}

export function HealthPage({
  health,
  prototype = false,
  sport = "NBA",
}: {
  health?: HealthView;
  prototype?: boolean;
  sport?: "NBA" | "NHL" | "NFL" | "MLB" | "SOCCER" | "NBL";
}) {
  const components = (health?.components ?? []).map((item) => {
    const row = item as Record<string, unknown>;
    return {
      name:
        typeof row.label === "string"
          ? row.label
          : typeof row.component === "string"
            ? row.component
            : "Unknown",
      status: typeof row.status === "string" ? row.status : "Unknown",
      detail:
        typeof row.detail === "string"
          ? row.detail
          : "Last checked moments ago",
    };
  });
  const groups = groupChecks(health?.systemHealth ?? []);
  const highlightly = health?.usage?.highlightly;
  const rapidodds = health?.usage?.rapidodds;
  const localAgent = health?.localAgent;
  return (
    <div className="board-grid">
      {prototype ? (
        <div className="board-wide board-actions">
          <span className="health-mode">Synthetic health rehearsal</span>
        </div>
      ) : null}
      {highlightly && rapidodds ? (
        <section
          className="provider-health card"
          aria-label="Provider allowances"
        >
          <div className="provider-health-heading">
            <div>
              <span className="eyebrow">Live allowances</span>
              <h3>Data providers</h3>
            </div>
            <small>Allowance is shared across every sport</small>
          </div>
          <article className="provider-health-row">
            <div className="provider-name">
              <strong>Highlightly</strong>
              <small>Schedule, results and T-30 lineups</small>
              {highlightly.allowanceMismatch &&
              highlightly.providerReportedLimit !== null &&
              highlightly.providerReportedLimit !== undefined ? (
                <small className="health-warning">
                  Plan mismatch: provider reports{" "}
                  {highlightly.providerReportedLimit} calls/day
                </small>
              ) : null}
            </div>
            <div className="provider-meter">
              <div>
                <span>Daily calls</span>
                <strong>
                  {highlightly.used} / {highlightly.hardLimit} calls
                </strong>
              </div>
              <progress value={highlightly.used} max={highlightly.hardLimit} />
              {resetsAt(highlightly.windowEndsAt) === null ? null : (
                <small>resets {resetsAt(highlightly.windowEndsAt)}</small>
              )}
            </div>
            <div className="provider-event">
              <span>Last success</span>
              <strong>{melbourneTimestamp(highlightly.lastSuccessAt)}</strong>
            </div>
            <span className={usageStatus(highlightly).className}>
              {usageStatus(highlightly).label}
            </span>
          </article>
          <article className="provider-health-row">
            <div className="provider-name">
              <strong>RapidOdds</strong>
              <small>
                {rapidodds.budgetMode === "TRIAL"
                  ? "Trial credits · REST only"
                  : "Starter plan · REST only"}
              </small>
            </div>
            <div className="provider-meter">
              <div>
                <span>Daily / allowance</span>
                <strong>
                  {rapidodds.dailyUsed} / {rapidodds.dailyLimit} today
                </strong>
              </div>
              <progress
                value={rapidodds.dailyUsed}
                max={rapidodds.dailyLimit}
              />
              <small>
                {rapidodds.allowanceUsed.toLocaleString("en-AU")} /{" "}
                {rapidodds.allowanceLimit.toLocaleString("en-AU")}{" "}
                {rapidodds.budgetMode === "TRIAL"
                  ? "trial credits used"
                  : "monthly credits"}
                {resetsAt(rapidodds.windowEndsAt) === null
                  ? null
                  : ` · daily resets ${resetsAt(rapidodds.windowEndsAt)}`}
              </small>
            </div>
            <div className="provider-event">
              <span>Last success</span>
              <strong>{melbourneTimestamp(rapidodds.lastSuccessAt)}</strong>
            </div>
            <span className={usageStatus(rapidodds).className}>
              {usageStatus(rapidodds).label}
            </span>
          </article>
          {sport === "NBA" && localAgent ? (
            <article className="provider-health-row">
              <div className="provider-name">
                <strong>Windows NBA fallback</strong>
                <small>stats.nba.com at T-5 only</small>
              </div>
              <div className="provider-agent-fact">
                <span>Device</span>
                <strong>{localAgent.displayName ?? "Not enrolled"}</strong>
              </div>
              <div className="provider-event">
                <span>Last heartbeat</span>
                <strong>{melbourneTimestamp(localAgent.lastSeenAt)}</strong>
                <small>
                  {localAgent.pendingJobs} pending job
                  {localAgent.pendingJobs === 1 ? "" : "s"}
                </small>
              </div>
              <span
                className={
                  localAgent.status === "ONLINE" ? "health-ok" : "health-warn"
                }
              >
                {localAgent.status === "ONLINE"
                  ? "Available"
                  : localAgent.status === "OFFLINE"
                    ? "PC offline"
                    : "Not enrolled"}
              </span>
            </article>
          ) : null}
        </section>
      ) : null}
      {(health?.systemHealth ?? []).length > 0 ? (
        <section className="health-list card board-wide">
          <h3>Every check, all sports</h3>
          <HealthGroup title="Needs attention" rows={groups.attention} open />
          <HealthGroup title="Degraded" rows={groups.degraded} open />
          <HealthGroup
            title="Idle - nothing to do in the last 24 hours"
            rows={groups.idle}
          />
          <HealthGroup title="Out of season" rows={groups.outOfSeason} />
          <HealthGroup title="Healthy" rows={groups.healthy} />
        </section>
      ) : null}
      <details className="health-list card">
        <summary>{sport} release checklist</summary>
        {components.map(({ name, status, detail }) => (
          <article key={name}>
            <div>
              <strong>{name}</strong>
              <small>{detail}</small>
            </div>
            <span
              className={
                status === "HEALTHY" || status === "APPROVED"
                  ? "health-ok"
                  : "health-warn"
              }
            >
              {status}
            </span>
          </article>
        ))}
      </details>
    </div>
  );
}
